import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import api, { BRAND } from "@/lib/appApi";
import { countryName } from "@/lib/countries";
import { Flag } from "@/components/Flag";
import { GENDERS } from "@/lib/genders";
import { toast } from "sonner";
import { ArrowLeft, Crown, Lock, Camera, Mic, Globe, EyeOff, Trash2, ShieldCheck } from "lucide-react";

export default function Profile({ asOverlay = false, onClose }) {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const close = () => (asOverlay ? onClose && onClose() : navigate("/app"));
  const [cams, setCams] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState(localStorage.getItem("cam_device") || "");
  const [micId, setMicId] = useState(localStorage.getItem("mic_device") || "");
  const premium = user?.is_premium;

  useEffect(() => {
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).then((s) => s.getTracks().forEach((t) => t.stop())).catch(() => {});
        const devices = await navigator.mediaDevices.enumerateDevices();
        setCams(devices.filter((d) => d.kind === "videoinput"));
        setMics(devices.filter((d) => d.kind === "audioinput"));
      } catch (e) {}
    })();
  }, []);

  const save = async (patch) => {
    try {
      const res = await api.put("/profile", patch);
      setUser(res.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const pickCam = (id) => { setCamId(id); localStorage.setItem("cam_device", id); toast.success("Camera saved"); };
  const pickMic = (id) => { setMicId(id); localStorage.setItem("mic_device", id); toast.success("Microphone saved"); };

  const cancelSub = async () => {
    try {
      const res = await api.post("/subscription/cancel");
      setUser(res.data);
      toast.success("Subscription will end at the end of your paid period.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  const deleteAccount = async () => {
    try {
      await api.post("/account/delete");
      toast.success("Your account has been deleted.");
      setUser(null);
      window.location.href = "/";
    } catch (e) {
      toast.error("Could not delete account");
    }
  };

  return (
    <div className={asOverlay ? "fixed inset-0 z-50 bg-background overflow-y-auto" : "min-h-screen bg-background text-foreground"}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
        <button onClick={close} data-testid="profile-back-btn" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {asOverlay ? "Close" : "Back to chat"}
        </button>
        <div className="flex items-center gap-2">
          <img src={BRAND.logo} alt="logo" className="w-8 h-8 rounded-full" />
          <span className="font-heading font-extrabold">Random Cam <span className="brand-gradient-text">Chat</span></span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-6 space-y-6">
        <h1 className="font-heading text-3xl font-extrabold">Profile & settings</h1>

        {/* Identity */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-heading font-bold">Account</p>
              <p className="text-sm text-muted-foreground">{user?.name} · {user?.email}</p>
            </div>
            {premium ? <span className="flex items-center gap-1 text-amber-400 text-sm"><Crown className="w-4 h-4" /> Premium</span> : <span className="text-sm text-muted-foreground">Free</span>}
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-primary" />
            Detected location: <span className="font-medium inline-flex items-center gap-1.5">{user?.country ? <><Flag code={user.country} size={18} /> {countryName(user.country)}</> : "Unknown"}</span>
          </div>
        </section>

        {/* Gender */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <p className="font-heading font-bold">Your gender</p>
          <p className="text-xs text-muted-foreground -mt-1">Shown next to "Stranger" so others know who they're chatting with.</p>
          <div className="grid grid-cols-5 gap-2">
            {GENDERS.map((g) => (
              <button key={g.v} onClick={() => save({ gender: g.v })} data-testid={`profile-gender-${g.v}`}
                className={`text-sm px-2 py-2 rounded-lg border transition-colors ${user?.gender === g.v ? "border-primary bg-primary/15" : "border-border bg-secondary hover:bg-accent"}`}>
                {g.l}
              </button>
            ))}
          </div>
        </section>

        {/* Devices */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <p className="font-heading font-bold">Devices</p>
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><Camera className="w-4 h-4" /> Camera</Label>
            <Select value={camId} onValueChange={pickCam}>
              <SelectTrigger data-testid="camera-select" className="bg-secondary border-border"><SelectValue placeholder="Default camera" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {cams.length === 0 && <SelectItem value="none" disabled>No cameras found</SelectItem>}
                {cams.map((d, i) => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><Mic className="w-4 h-4" /> Microphone</Label>
            <Select value={micId} onValueChange={pickMic}>
              <SelectTrigger data-testid="mic-select" className="bg-secondary border-border"><SelectValue placeholder="Default microphone" /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                {mics.length === 0 && <SelectItem value="none" disabled>No microphones found</SelectItem>}
                {mics.map((d, i) => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${i + 1}`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">Device changes apply the next time you press Start.</p>
        </section>

        {/* Privacy */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <p className="font-heading font-bold">Privacy</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <EyeOff className="w-4 h-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Hide my country</p>
                <p className="text-xs text-muted-foreground">Show "Private" instead of your flag to strangers.</p>
              </div>
            </div>
            <Switch checked={!!user?.hide_country} onCheckedChange={(v) => save({ hide_country: v })} data-testid="hide-country-switch" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-400" />
              <div>
                <p className="text-sm font-medium">Hide my Premium badge</p>
                <p className="text-xs text-muted-foreground">Don't show the Premium badge to strangers.</p>
              </div>
            </div>
            <Switch checked={!!user?.hide_premium_badge} onCheckedChange={(v) => save({ hide_premium_badge: v })} data-testid="hide-premium-switch" />
          </div>
        </section>

        {/* Subscription */}
        {premium && !user?.is_admin && (
          <section className="rounded-2xl border border-border bg-card p-6 space-y-3">
            <p className="font-heading font-bold">Subscription</p>
            <p className="text-sm text-muted-foreground">
              Plan: <span className="text-foreground">{user?.subscription_plan?.replace("premium_", "") || "—"}</span>
              {user?.subscription_expires && <> · Renews/expires {new Date(user.subscription_expires).toLocaleDateString()}</>}
            </p>
            {user?.subscription_cancel_pending ? (
              <p className="text-sm text-amber-400">Cancellation scheduled — you keep Premium until the end of your paid period.</p>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="border-border bg-secondary" data-testid="cancel-sub-btn">Cancel subscription</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
                    <AlertDialogDescription>You'll keep Premium features until the end of your current paid period, then return to the free tier. No refund is issued for the remaining time.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Premium</AlertDialogCancel>
                    <AlertDialogAction onClick={cancelSub} data-testid="confirm-cancel-sub">Cancel subscription</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </section>
        )}

        {/* Danger zone */}
        <section className="rounded-2xl border border-destructive/40 bg-destructive/5 p-6 space-y-3">
          <p className="font-heading font-bold text-destructive flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete account</p>
          <p className="text-sm text-muted-foreground">Permanently deletes your login. Your email can be used again later (unless banned). As required for safety and legal compliance, we retain a minimal record (email, any reports and payment history). See our <button onClick={() => navigate("/privacy")} className="text-primary underline">Privacy Policy</button>.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive/10" data-testid="delete-account-btn">Delete my account</Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-card border-border">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your account permanently?</AlertDialogTitle>
                <AlertDialogDescription>This removes your login and profile. This cannot be undone. A minimal compliance record is retained as described in our Privacy Policy.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep my account</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} data-testid="confirm-delete-account" className="bg-destructive hover:bg-destructive/90 text-white">Delete forever</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </section>

        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2">
          <ShieldCheck className="w-4 h-4" /> Your privacy matters. We only store your email and name for account usability.
        </div>
      </main>
    </div>
  );
}
